/**
 * pages/SettingsPage.tsx
 * Author: Sean Johnson <sean@scj.net>
 * Purpose: Admin user list — auth.scj.net/settings
 * Last Updated: 2026-05-31
 * Version: 1.0.0
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuth'
import { adminApi, type AdminUser, ALL_APP_GROUPS, APP_LABELS, APP_COLORS } from '../hooks/useAdmin'

export function SettingsPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteGroups, setInviteGroups] = useState<string[]>([])
  const [inviting, setInviting] = useState(false)
  const [inviteMsg, setInviteMsg] = useState('')

  useEffect(() => {
    loadUsers()
  }, [])

  async function loadUsers() {
    try {
      setLoading(true)
      const list = await adminApi.listUsers()
      setUsers(list)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault()
    if (!inviteEmail) return
    setInviting(true)
    setInviteMsg('')
    try {
      await adminApi.createUser(inviteEmail, inviteGroups)
      setInviteMsg(`Invite sent to ${inviteEmail}`)
      setInviteEmail('')
      setInviteGroups([])
      await loadUsers()
    } catch (err: any) {
      setInviteMsg(`Error: ${err.message}`)
    } finally {
      setInviting(false)
    }
  }

  function toggleInviteGroup(group: string) {
    setInviteGroups(prev =>
      prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group]
    )
  }

  if (loading) {
    return (
      <div className="settings-container">
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    )
  }

  return (
    <div className="settings-container">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-sm text-gray-500 mt-1">Logged in as {user?.email}</p>
        </div>
        <button
          onClick={() => navigate('/profile')}
          className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          ← Back to profile
        </button>
      </div>

      {error && <div className="auth-error">{error}</div>}

      {/* Invite form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Invite User</h2>
        <form onSubmit={handleInvite} className="space-y-4">
          <div className="flex gap-3">
            <input
              type="email"
              placeholder="user@example.com"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              className="auth-input flex-1"
              required
            />
            <button
              type="submit"
              disabled={inviting}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {inviting ? 'Sending…' : 'Invite'}
            </button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {ALL_APP_GROUPS.map(group => (
              <button
                key={group}
                type="button"
                onClick={() => toggleInviteGroup(group)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  inviteGroups.includes(group)
                    ? APP_COLORS[group] + ' border-transparent'
                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-400'
                }`}
              >
                {APP_LABELS[group]}
              </button>
            ))}
          </div>
          {inviteMsg && (
            <p className={`text-sm ${inviteMsg.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
              {inviteMsg}
            </p>
          )}
        </form>
      </div>

      {/* User table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">App Access</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map(u => (
              <tr key={u.sub} className="hover:bg-gray-50 transition-colors">
                <td className="px-5 py-3">
                  <div className="font-medium text-gray-900">{u.email}</div>
                  {u.groups.includes('admin') && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${APP_COLORS['admin']}`}>admin</span>
                  )}
                </td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center gap-1 text-xs font-medium ${u.enabled ? 'text-green-700' : 'text-gray-400'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${u.enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                    {u.enabled ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <div className="flex gap-1.5 flex-wrap">
                    {ALL_APP_GROUPS.map(group => (
                      <span
                        key={group}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          u.groups.includes(group) ? APP_COLORS[group] : 'bg-gray-100 text-gray-300'
                        }`}
                      >
                        {APP_LABELS[group]}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-5 py-3 text-gray-400 text-xs">
                  {new Date(u.created).toLocaleDateString()}
                </td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={() => navigate(`/settings/users/${u.sub}`)}
                    className="text-blue-600 hover:text-blue-800 font-medium text-xs transition-colors"
                  >
                    Edit →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && (
          <div className="text-center py-10 text-gray-400 text-sm">No users found</div>
        )}
      </div>

      <p className="text-xs text-gray-400 mt-4 text-center">{users.length} user{users.length !== 1 ? 's' : ''}</p>
    </div>
  )
}
